import {
  Box,
  Button,
  ButtonProps,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
  TextProps,
} from "@chakra-ui/react";
import { format } from "date-fns";
import { useRouter } from "next/router";
import { memo, useEffect, useMemo, useState } from "react";

import { getPageRoutes } from "@/routes/pageRoutes";
import { getLocalStorageData } from "@picovico/utils";
import { useGetNowShowingQuery } from "store/services/movie";
import { ShowInfo } from "../../../@types/movie";

const TODAY = format(new Date(), "yyyy-MM-dd");

const DATE_OPTIONS = Array.from({ length: 5 }, (_, i) => {
  const d = new Date();
  d.setDate(d.getDate() + i);
  const value = format(d, "yyyy-MM-dd");
  const label = i === 0 ? "Today" : i === 1 ? "Tomorrow" : format(d, "MMM dd");
  return { value, label };
});

export interface ChangeShowModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentMovieIdx?: string;
  titleProps?: TextProps;
  timeSlotButtonProps?: ButtonProps;
}

const ChangeShowModal = memo(function ChangeShowModal({
  isOpen,
  onClose,
  currentMovieIdx,
  titleProps,
  timeSlotButtonProps,
}: ChangeShowModalProps) {
  const router = useRouter();
  const cinema = getLocalStorageData("cinema_hall") ?? "";

  const { data: moviesData, isLoading } = useGetNowShowingQuery(
    {
      show_date: TODAY,
      cinema_hall: cinema,
      advance_days: true,
    },
    { skip: !cinema },
  );

  const movies = useMemo(() => moviesData?.data || [], [moviesData]);

  const [selectedMovieIdx, setSelectedMovieIdx] = useState(
    currentMovieIdx || "",
  );
  const [selectedAudi, setSelectedAudi] = useState("");
  const [selectedDate, setSelectedDate] = useState(TODAY);

  useEffect(() => {
    if (movies.length > 0 && !selectedMovieIdx) {
      setSelectedMovieIdx(currentMovieIdx || movies[0].idx);
    }
  }, [movies, currentMovieIdx, selectedMovieIdx]);

  const selectedMovie = useMemo(
    () => movies.find((m) => m.idx === selectedMovieIdx),
    [movies, selectedMovieIdx],
  );

  const audiOptions = useMemo(() => {
    if (!selectedMovie) return [];
    return Array.from(
      new Set(selectedMovie.showinfo.map((s) => s.auditorium.name)),
    );
  }, [selectedMovie]);

  const timeSlots = useMemo(() => {
    if (!selectedMovie) return [];
    return selectedMovie.showinfo
      .filter((show) => {
        const date = format(show.show_start_date_time, "yyyy-MM-dd");
        const audiMatch =
          !selectedAudi || show.auditorium.name === selectedAudi;
        return date === selectedDate && audiMatch;
      })
      .sort((a, b) =>
        a.show_start_date_time.localeCompare(b.show_start_date_time),
      );
  }, [selectedMovie, selectedDate, selectedAudi]);

  const handleMovieChange = (idx: string) => {
    setSelectedMovieIdx(idx);
    setSelectedAudi("");
  };

  const handleSlotClick = (show: ShowInfo) => {
    if (
      show.show_status === "expired" ||
      show.show_status === "sold" ||
      show.seat_view_allowed === false
    )
      return;
    router.replace(
      getPageRoutes("audi_detail", {
        movieIdx: selectedMovieIdx,
        showIdx: show.show_idx,
      }),
    );
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
      <ModalOverlay />
      <ModalContent borderRadius="xl" overflow="hidden">
        <ModalHeader borderBottom="1px solid" borderColor="gray.100" pb={3}>
          <Text fontSize="lg" fontWeight="semibold" {...titleProps}>
            Choose Your Show
          </Text>
        </ModalHeader>
        <ModalCloseButton
          top={3}
          right={3}
          borderRadius="full"
          bg="red.500"
          color="white"
          size="sm"
          _hover={{ bg: "red.600" }}
        />

        <ModalBody p={5}>
          {isLoading ? (
            <Flex justify="center" align="center" h="200px">
              <Spinner size="lg" />
            </Flex>
          ) : (
            <Flex gap={5} direction={{ base: "column", sm: "row" }}>
              {/* Poster */}
              <Box
                position="relative"
                flexShrink={0}
                w={{ base: "100%", sm: "40%" }}
              >
                <Image
                  src={selectedMovie?.poster}
                  alt={selectedMovie?.name}
                  w="full"
                  h={{ base: "200px", sm: "auto" }}
                  objectFit="cover"
                  borderRadius="lg"
                />
                {selectedMovie?.grade && (
                  <Flex
                    position="absolute"
                    top={2}
                    right={2}
                    w="34px"
                    h="34px"
                    borderRadius="full"
                    bg="blackAlpha.700"
                    color="white"
                    align="center"
                    justify="center"
                    fontSize="xs"
                    fontWeight="bold"
                  >
                    {selectedMovie.grade}
                  </Flex>
                )}
              </Box>

              {/* Form */}
              <Flex direction="column" flex={1} gap={3}>
                <Select
                  borderRadius="md"
                  value={selectedMovieIdx}
                  onChange={(e) => handleMovieChange(e.target.value)}
                  isDisabled={movies.length === 0}
                >
                  {movies.map((m) => (
                    <option key={m.idx} value={m.idx}>
                      {m.name}
                    </option>
                  ))}
                </Select>

                <Select
                  borderRadius="md"
                  placeholder="All Auditoriums"
                  value={selectedAudi}
                  onChange={(e) => setSelectedAudi(e.target.value)}
                  isDisabled={audiOptions.length === 0}
                >
                  {audiOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </Select>

                <Select
                  borderRadius="md"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                >
                  {DATE_OPTIONS.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </Select>

                {/* Time slots */}
                <Box>
                  {timeSlots.length === 0 ? (
                    <Text fontSize="sm" color="gray.500">
                      No shows available for this date
                    </Text>
                  ) : (
                    <Flex gap={2} flexWrap="wrap">
                      {timeSlots.map((show) => {
                        const expired =
                          show.show_status === "expired" ||
                          show.show_status === "sold" ||
                          show.seat_view_allowed === false;
                        return (
                          <Button
                            key={show.show_idx}
                            size="sm"
                            borderRadius="full"
                            colorScheme="primary"
                            opacity={expired ? 0.5 : 1}
                            cursor={expired ? "not-allowed" : "pointer"}
                            onClick={() => handleSlotClick(show)}
                            {...timeSlotButtonProps}
                          >
                            {format(show.show_start_date_time, "hh:mm a")}
                          </Button>
                        );
                      })}
                    </Flex>
                  )}
                </Box>
              </Flex>
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
});

export default ChangeShowModal;
